import React, { useState, useEffect } from 'react';
import { X, ThumbsUp, MessageSquare, Send, Heart } from 'lucide-react';
import ShareButton from './ShareButton';
import { getComments, createComment, getFeatureRequests, createFeatureRequest, voteFeatureRequest, likeComment } from '../services/firestore';

export default function ProductDetailModal({ isOpen, onClose, item, user, onVote }) {
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);

  const [featureRequest, setFeatureRequest] = useState('');
  const [features, setFeatures] = useState([]);
  const [loadingFeatures, setLoadingFeatures] = useState(false);

  // Local state for the item to track vote changes
  const [localItem, setLocalItem] = useState(item);

  // Update local item when prop changes
  useEffect(() => {
    if (item) {
      setLocalItem(item);
    }
  }, [item]);

  // Load comments and features when modal opens or item changes
  useEffect(() => {
    if (isOpen && item?.id) {
      // Clear old data immediately when item changes
      setComments([]);
      setFeatures([]);
      setComment('');
      setFeatureRequest('');

      // Load new data using current item.id
      loadCommentsForItem(item.id);
      loadFeaturesForItem(item.id);
    }
  }, [isOpen, item?.id]);

  const loadCommentsForItem = async (itemId) => {
    if (!itemId) return;
    setLoadingComments(true);
    try {
      const commentsData = await getComments(itemId);
      setComments(commentsData);
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const loadComments = async () => {
    if (!localItem?.id) return;
    await loadCommentsForItem(localItem.id);
  };

  const loadFeaturesForItem = async (itemId) => {
    if (!itemId) return;
    setLoadingFeatures(true);
    try {
      const featuresData = await getFeatureRequests(itemId);
      setFeatures(featuresData);
    } catch (error) {
      console.error('Error loading features:', error);
    } finally {
      setLoadingFeatures(false);
    }
  };

  const loadFeatures = async () => {
    if (!localItem?.id) return;
    await loadFeaturesForItem(localItem.id);
  };

  if (!isOpen || !item) return null;

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!user) {
      alert('Please login to comment!');
      return;
    }
    if (!comment.trim()) return;

    try {
      const userName = user.displayName || user.email?.split('@')[0] || 'Anonymous';
      await createComment(item.id, 'huntItem', user.uid, userName, comment);
      setComment('');
      await loadComments();
    } catch (error) {
      console.error('Error posting comment:', error);
      alert('Failed to post comment. Please try again.');
    }
  };

  const handleSubmitFeature = async (e) => {
    e.preventDefault();
    if (!user) {
      alert('Please login to request features!');
      return;
    }
    if (!featureRequest.trim()) return;

    try {
      const userName = user.displayName || user.email?.split('@')[0] || 'Anonymous';
      await createFeatureRequest(item.id, user.uid, userName, featureRequest);
      setFeatureRequest('');
      await loadFeatures();
    } catch (error) {
      console.error('Error requesting feature:', error);
      alert('Failed to request feature. Please try again.');
    }
  };

  const handleVoteFeature = async (featureId) => {
    if (!user) {
      alert('Please login to vote!');
      return;
    }

    try {
      await voteFeatureRequest(featureId, user.uid);
      await loadFeatures();
    } catch (error) {
      console.error('Error voting feature:', error);
      alert('Failed to vote. Please try again.');
    }
  };

  const handleVoteIdea = async (voteType) => {
    if (!user) {
      alert('Please login to vote!');
      return;
    }

    // Optimistic update for the local item
    const hasUpvoted = localItem.upvoters?.includes(user.uid);
    const hasDownvoted = localItem.downvoters?.includes(user.uid);

    let newUpvoters = [...(localItem.upvoters || [])];
    let newDownvoters = [...(localItem.downvoters || [])];

    if (voteType === 'up') {
      if (hasUpvoted) {
        newUpvoters = newUpvoters.filter(id => id !== user.uid);
      } else {
        newUpvoters.push(user.uid);
        newDownvoters = newDownvoters.filter(id => id !== user.uid);
      }
    } else {
      if (hasDownvoted) {
        newDownvoters = newDownvoters.filter(id => id !== user.uid);
      } else {
        newDownvoters.push(user.uid);
        newUpvoters = newUpvoters.filter(id => id !== user.uid);
      }
    }

    const newVotes = newUpvoters.length - newDownvoters.length;

    setLocalItem({
      ...localItem,
      upvoters: newUpvoters,
      downvoters: newDownvoters,
      votes: newVotes
    });

    try {
      if (onVote) {
        await onVote(localItem.id, user.uid, voteType);
      }
    } catch (error) {
      console.error('Error voting:', error);
      // Revert on error
      setLocalItem(item);
    }
  };

  const handleLikeComment = async (commentId) => {
    if (!user) {
      alert('Please login to like comments!');
      return;
    }

    // Optimistic update
    setComments(prevComments =>
      prevComments.map(comment => {
        if (comment.id === commentId) {
          const hasLiked = comment.likers?.includes(user.uid);
          const newLikers = hasLiked
            ? (comment.likers || []).filter(id => id !== user.uid)
            : [...(comment.likers || []), user.uid];
          return {
            ...comment,
            likers: newLikers,
            likes: newLikers.length
          };
        }
        return comment;
      })
    );

    try {
      await likeComment(commentId, user.uid);
      // Reload to get accurate data and resort
      await loadComments();
    } catch (error) {
      console.error('Error liking comment:', error);
      alert('Failed to like comment. Please try again.');
      // Revert on error
      await loadComments();
    }
  };

  const hasUpvoted = user && localItem?.upvoters?.includes(user.uid);
  const hasDownvoted = user && localItem?.downvoters?.includes(user.uid);

  const timeAgo = (date) => {
    if (!date) return 'recently';
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 md:p-4" onClick={onClose}>
      <div className="bg-slate-900 rounded-2xl max-w-4xl w-full max-h-[95vh] md:max-h-[90vh] flex flex-col border border-slate-800 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Close Button - Always Visible */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 md:top-4 md:right-4 text-slate-400 hover:text-white transition z-50 bg-slate-900/80 backdrop-blur-sm rounded-full p-2"
        >
          <X className="w-5 h-5 md:w-6 md:h-6" />
        </button>

        {/* Header */}
        <div className="sticky top-0 bg-slate-900/95 backdrop-blur-sm border-b border-slate-800 p-4 md:p-6 shrink-0">
          <div className="flex items-center gap-3 md:gap-4 pr-8">
            <div className={`w-12 h-12 md:w-16 md:h-16 rounded-xl flex items-center justify-center text-3xl md:text-4xl shadow-lg bg-gradient-to-br ${localItem?.color} shrink-0`}>
              {localItem?.icon}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg md:text-2xl font-bold text-white mb-1 break-words">{localItem?.title}</h2>
              <p className="text-slate-400 text-xs md:text-sm">by @{localItem?.author}</p>
            </div>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="p-4 md:p-6 space-y-4 md:space-y-6 overflow-y-auto flex-1">
          {/* Description */}
          <div>
            <p className="text-slate-300 text-base md:text-lg leading-relaxed">{localItem?.desc}</p>
            <div className="flex flex-wrap gap-2 mt-3 md:mt-4">
              {localItem?.tags?.map(tag => (
                <span key={tag} className="bg-slate-800 px-2 md:px-3 py-1 rounded-full text-xs md:text-sm text-slate-400">
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-3 md:gap-6 pb-4 md:pb-6 border-b border-slate-800">
            <button
              onClick={() => handleVoteIdea('up')}
              className={`px-3 md:px-4 py-2 rounded-lg flex items-center gap-2 transition text-sm md:text-base ${
                hasUpvoted
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white'
              }`}
            >
              <ThumbsUp className="w-4 h-4" />
              <span className="font-bold">{localItem?.votes || 0}</span>
            </button>
            <ShareButton
              title={localItem?.title}
              description={localItem?.desc}
              itemId={localItem?.id}
              itemType="hunt"
              className="px-3 md:px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-xs md:text-sm font-medium"
            />
            <span className="text-slate-500 text-xs md:text-sm ml-auto">
              {comments.length} comments
            </span>
          </div>

          {/* Feature Requests */}
          <div>
            <h3 className="text-lg md:text-xl font-bold text-white mb-3 md:mb-4">Funny Feature Requests</h3>
            {loadingFeatures ? (
              <div className="text-slate-500 text-center py-4 text-sm">Loading features...</div>
            ) : (
              <div className="space-y-2 md:space-y-3 mb-3 md:mb-4">
                {features.length === 0 ? (
                  <div className="text-slate-500 text-center py-4 text-sm">No feature requests yet. Be the first!</div>
                ) : (
                  features.map(feature => {
                    const hasVoted = user && feature.voters?.includes(user.uid);
                    return (
                      <div key={feature.id} className="bg-slate-800/50 p-3 md:p-4 rounded-lg flex items-start md:items-center gap-3 justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm md:text-base break-words">{feature.text}</p>
                          <p className="text-xs text-slate-500 mt-1">by {feature.userName}</p>
                        </div>
                        <button
                          onClick={() => handleVoteFeature(feature.id)}
                          className={`flex items-center gap-1 md:gap-2 px-2 md:px-3 py-1 rounded text-xs md:text-sm transition shrink-0 ${
                            hasVoted
                              ? 'bg-emerald-500/20 text-emerald-400'
                              : 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                          }`}
                        >
                          <ThumbsUp className="w-3 h-3" />
                          {feature.votes}
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            )}
            <form onSubmit={handleSubmitFeature} className="flex flex-col md:flex-row gap-2">
              <input
                type="text"
                value={featureRequest}
                onChange={(e) => setFeatureRequest(e.target.value)}
                placeholder="Request an absurd feature..."
                className="flex-1 bg-slate-800 text-white px-3 md:px-4 py-2 rounded-lg border border-slate-700 focus:border-amber-500 focus:outline-none text-sm md:text-base"
              />
              <button
                type="submit"
                className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold px-3 md:px-4 py-2 rounded-lg transition flex items-center justify-center gap-2 text-sm md:text-base"
              >
                <Send className="w-4 h-4" />
                <span className="md:inline">Request</span>
              </button>
            </form>
          </div>

          {/* Comments */}
          <div>
            <h3 className="text-lg md:text-xl font-bold text-white mb-3 md:mb-4">Comments</h3>
            {loadingComments ? (
              <div className="text-slate-500 text-center py-4 text-sm">Loading comments...</div>
            ) : (
              <div className="space-y-3 md:space-y-4 mb-3 md:mb-4">
                {comments.length === 0 ? (
                  <div className="text-slate-500 text-center py-4 text-sm">No comments yet. Be the first!</div>
                ) : (
                  comments.map(comment => {
                    const hasLiked = user && comment.likers?.includes(user.uid);
                    return (
                      <div key={comment.id} className="bg-slate-800/30 p-3 md:p-4 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-slate-700 to-slate-600 flex items-center justify-center text-xs font-bold shrink-0">
                            {comment.userName?.[0] || 'U'}
                          </div>
                          <span className="font-bold text-white text-xs md:text-sm truncate">{comment.userName}</span>
                          <span className="text-xs text-slate-500 shrink-0">{timeAgo(comment.createdAt)}</span>
                          <button
                            onClick={() => handleLikeComment(comment.id)}
                            className={`ml-auto flex items-center gap-1 text-xs transition shrink-0 ${
                              hasLiked ? 'text-red-500' : 'text-slate-400 hover:text-red-400'
                            }`}
                          >
                            <Heart className={`w-4 h-4 ${hasLiked ? 'fill-current' : ''}`} />
                            <span>{comment.likes || 0}</span>
                          </button>
                        </div>
                        <p className="text-slate-300 text-sm md:text-base break-words">{comment.text}</p>
                      </div>
                    );
                  })
                )}
              </div>
            )}
            <form onSubmit={handleSubmitComment} className="flex flex-col md:flex-row gap-2">
              <input
                type="text"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add a comment..."
                className="flex-1 bg-slate-800 text-white px-3 md:px-4 py-2 rounded-lg border border-slate-700 focus:border-amber-500 focus:outline-none text-sm md:text-base"
              />
              <button
                type="submit"
                className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold px-3 md:px-4 py-2 rounded-lg transition flex items-center justify-center gap-2 text-sm md:text-base"
              >
                <MessageSquare className="w-4 h-4" />
                <span>Post</span>
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
